import { useEffect, useRef, useState } from 'react';
import { useTurnstile } from './useTurnstile';
import { useParams } from 'react-router-dom';
import type { WidgetPublicConfig } from '@vd/types';
import { Loader2, CheckCircle2, AlertCircle } from 'lucide-react';
import styles from './ReportLinkPage.module.css';

/**
 * Toado-hosted bug report form.
 *
 * For reporters who cannot run the snippet: a client emailed a link, a QR
 * code on a kiosk, a tester on a device where the site is not
 * instrumented. Same ingestion pipeline as the embedded widget, different
 * front door, `source: 'public_link'` on the ticket.
 *
 * Deliberately standalone:
 *   - No app chrome, no auth. The visitor is a stranger; showing them a
 *     sign-in affordance or a workspace switcher would be nonsense.
 *   - No screenshot rasterization. There is no host page to photograph —
 *     the reporter attaches an image if they have one, which on a phone
 *     is the far more natural gesture anyway.
 *   - It uses the app's own fetch rather than the widget SDK, because the
 *     SDK's whole job (shadow DOM isolation, launcher, host-page capture)
 *     is irrelevant when we own the page.
 *
 * Spec: docs/prds/EMBEDDED_WIDGET_PRD.md §3.8.
 */

/** Matches the api's per-part ceiling for an attached image. */
const MAX_ATTACHMENT_BYTES = 8 * 1024 * 1024;

type Phase = 'loading' | 'unavailable' | 'ready' | 'sending' | 'sent';

export function ReportLinkPage() {
  const { key = '' } = useParams();
  const [phase, setPhase] = useState<Phase>('loading');
  const [config, setConfig] = useState<WidgetPublicConfig | null>(null);
  const [description, setDescription] = useState('');
  const [email, setEmail] = useState('');
  const [fieldValues, setFieldValues] = useState<Record<string, string>>({});
  const [attachment, setAttachment] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [receipt, setReceipt] = useState<string | undefined>();

  /**
   * Stable for the life of this page, so a retry after a network wobble
   * cannot file a second ticket for the same report.
   */
  const idempotencyKey = useRef(
    `link-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`,
  );

  // Mounted as soon as the config names a site key. In production the
  // server REQUIRES a token, so a page without this rejects every
  // submission — and does so invisibly in local development, where the
  // captcha is deliberately disabled.
  const captcha = useTurnstile(config?.captcha?.siteKey ?? null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const res = await fetch(`/api/v1/public/widgets/${encodeURIComponent(key)}/link-config`, {
          credentials: 'omit',
        });
        if (!res.ok) throw new Error('unavailable');
        const body = await res.json() as WidgetPublicConfig;
        if (cancelled) return;
        setConfig(body);
        setPhase('ready');
      } catch {
        if (!cancelled) setPhase('unavailable');
      }
    })();
    return () => { cancelled = true; };
  }, [key]);

  useEffect(() => {
    if (config?.dialog.title) document.title = config.dialog.title;
  }, [config?.dialog.title]);

  if (phase === 'loading') {
    return (
      <main className={styles.page}>
        <div className={styles.card}>
          <Loader2 className={styles.spin} aria-hidden />
          <p>Loading…</p>
        </div>
      </main>
    );
  }

  if (phase === 'unavailable' || !config) {
    return (
      <main className={styles.page}>
        <div className={styles.card}>
          <AlertCircle className={styles.errorIcon} aria-hidden />
          <h1 className={styles.title}>This report link is not available</h1>
          <p className={styles.muted}>
            It may have been turned off, or the address may be incomplete. Check with
            whoever shared it with you.
          </p>
        </div>
      </main>
    );
  }

  if (phase === 'sent') {
    return (
      <main className={styles.page}>
        <div className={styles.card}>
          <CheckCircle2 className={styles.okIcon} aria-hidden />
          <h1 className={styles.title}>{config.dialog.successMessage}</h1>
          {receipt && (
            <>
              <p className={styles.muted}>Quote this if you follow up:</p>
              <p className={styles.receipt}>{receipt}</p>
            </>
          )}
        </div>
      </main>
    );
  }

  const submit = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    setError(null);

    if (!description.trim()) {
      setError('Please describe what went wrong.');
      return;
    }
    if (config.dialog.emailMode === 'required' && !email.trim()) {
      setError('Please enter your email address.');
      return;
    }
    for (const field of config.fields) {
      if (field.required && !fieldValues[field.key]) {
        setError(`Please complete "${field.label}".`);
        return;
      }
    }
    if (attachment && attachment.size > MAX_ATTACHMENT_BYTES) {
      setError('That image is too large. Please attach one under 8 MB.');
      return;
    }

    setPhase('sending');
    try {
      const form = new FormData();
      form.set('description', description.trim());
      // There is no host page here, so the "page" this report is about is
      // whatever the reporter describes. Recording the link URL keeps the
      // ticket's sourceUrl meaningful rather than empty.
      form.set('pageUrl', window.location.href);
      form.set('configVersion', String(config.version));
      if (email.trim()) form.set('reporterEmail', email.trim());
      for (const [k, v] of Object.entries(fieldValues)) {
        if (v) form.set(`field:${k}`, v);
      }
      form.set('browser', JSON.stringify({
        userAgent: navigator.userAgent,
        viewport: { width: window.innerWidth, height: window.innerHeight },
        devicePixelRatio: window.devicePixelRatio || 1,
      }));
      if (attachment) form.set('screenshot', attachment, attachment.name);

      const captchaToken = await captcha.getToken();
      const headers: Record<string, string> = {
        'idempotency-key': idempotencyKey.current,
      };
      // Sent as a header so the server can verify it before reading the
      // (potentially multi-megabyte) body.
      if (captchaToken) headers['x-toado-captcha'] = captchaToken;

      const res = await fetch(
        `/api/v1/public/widgets/${encodeURIComponent(config.key)}/link-submissions`,
        { method: 'POST', credentials: 'omit', headers, body: form },
      );

      if (!res.ok) {
        const body = await res.json().catch(() => ({})) as { code?: string };
        setError(messageFor(body.code, res.status));
        setPhase('ready');
        return;
      }
      const body = await res.json().catch(() => ({})) as { receipt?: string };
      setReceipt(body.receipt);
      setPhase('sent');
    } catch {
      setError('We could not send that report. Please check your connection and try again.');
      setPhase('ready');
    }
  };

  const sending = phase === 'sending';

  return (
    <main className={styles.page}>
      <form className={styles.card} onSubmit={submit} noValidate>
        <h1 className={styles.title}>{config.dialog.title}</h1>
        {config.dialog.description && (
          <p className={styles.muted}>{config.dialog.description}</p>
        )}

        <label className={styles.label} htmlFor="report-description">
          What would you like to tell us? <span className={styles.required}>*</span>
        </label>
        <textarea
          id="report-description"
          className={styles.textarea}
          value={description}
          maxLength={5000}
          required
          placeholder="For a bug, what you expected and what happened instead. For an idea, what you would like and why."
          onChange={(e) => setDescription(e.target.value)}
        />

        {config.dialog.emailMode !== 'hidden' && (
          <>
            <label className={styles.label} htmlFor="report-email">
              Your email{' '}
              {config.dialog.emailMode === 'required'
                ? <span className={styles.required}>*</span>
                : <span className={styles.optional}>(optional)</span>}
            </label>
            <input
              id="report-email"
              className={styles.input}
              type="email"
              autoComplete="email"
              maxLength={254}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </>
        )}

        {config.fields.map((field) => (
          <div key={field.key}>
            <label className={styles.label} htmlFor={`report-${field.key}`}>
              {field.label} {field.required && <span className={styles.required}>*</span>}
            </label>
            {field.type === 'select' ? (
              <select
                id={`report-${field.key}`}
                className={styles.input}
                value={fieldValues[field.key] ?? ''}
                onChange={(e) => setFieldValues((v) => ({ ...v, [field.key]: e.target.value }))}
              >
                <option value="">Choose…</option>
                {(field.options ?? []).map((option) => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
            ) : (
              <input
                id={`report-${field.key}`}
                className={styles.input}
                type={field.type === 'email' ? 'email' : 'text'}
                maxLength={500}
                placeholder={field.placeholder}
                value={fieldValues[field.key] ?? ''}
                onChange={(e) => setFieldValues((v) => ({ ...v, [field.key]: e.target.value }))}
              />
            )}
          </div>
        ))}

        <label className={styles.label} htmlFor="report-attachment">
          Add a screenshot <span className={styles.optional}>(optional)</span>
        </label>
        {/* `capture` is deliberately absent: on a phone this lets the
            reporter pick an existing screenshot, which is what they
            actually have, rather than forcing the camera open. */}
        <input
          id="report-attachment"
          className={styles.input}
          type="file"
          accept="image/png,image/jpeg,image/webp"
          onChange={(e) => setAttachment(e.target.files?.[0] ?? null)}
        />

        {/* Space is reserved once Cloudflare escalates to an interactive
            challenge, so it cannot shove the submit button off-screen. */}
        <div
          ref={captcha.containerRef}
          className={captcha.interactive ? styles.captchaActive : styles.captcha}
        />

        {error && (
          <p className={styles.error} role="alert">{error}</p>
        )}

        <button type="submit" className={styles.submit} disabled={sending}>
          {sending ? 'Sending…' : config.dialog.submitLabel}
        </button>

        <p className={styles.hostedBy}>
          {/* This page renders text supplied by whoever owns the widget,
              on Toado's own domain, with no sign-in required. Without a
              persistent marker it is a convincing surface for
              impersonating Toado itself. */}
          Hosted by Toado on behalf of the team that shared this link.
        </p>

        <p className={styles.privacy}>
          {config.privacy.noticeText
            ?? (config.privacy.ownerName
              ? `Sending this report shares what you write, any image you attach, and your browser details with ${config.privacy.ownerName}, via Toado.`
              : 'Sending this report shares what you write, any image you attach, and your browser details with this site’s team, via Toado.')}
          {config.privacy.policyUrl && (
            <>
              {' '}
              <a href={config.privacy.policyUrl} target="_blank" rel="noopener noreferrer">
                Privacy policy
              </a>
            </>
          )}
        </p>
      </form>
    </main>
  );
}

/**
 * Reporter-facing copy. Mirrors the SDK's mapping: a stranger cannot act
 * on "the workspace hit its monthly cap", and telling them would leak the
 * customer's billing state.
 */
function messageFor(code: string | undefined, status: number): string {
  switch (code) {
    case 'reporting_paused':
    case 'widget_disabled':
    case 'widget_not_configured':
      return 'Reporting is temporarily unavailable. Please try again later.';
    case 'captcha_failed':
      return 'We could not verify that you are human. Please try again.';
    case 'rate_limited':
      return 'Too many reports have been sent recently. Please try again in a few minutes.';
    case 'payload_too_large':
      return 'That attachment is too large. Try again with a smaller image.';
    case 'validation_error':
      return 'Please check the form and try again.';
    default:
      return status >= 500
        ? 'Something went wrong on our end. Please try again shortly.'
        : 'We could not send that report. Please try again.';
  }
}
